import './ui/search-v3-ui-redirect';
import './ui/search-v3-ui-support';
import './ui/search-v3-ui-search-page';
import { html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { getSuggestionsApiCall } from '../search/api/suggestion.api';
import { getSuggestionsApiCall as getSuggestionsV2ApiCall } from '../search-v2/api/suggestion-v2.apis';
import { SearchResultPage, SearchTermRedirects, SearchV3Result, SearchV3SupportResult } from './search-v3.interface';
import { getSearchTermRedirect, searchV3SuggestionMapper, searchV2SuggestionMapper } from './search-response.mapper';
import { SearchV3Analytics } from './search.analytic';
import { BUSINESS_UNIT_MAP, BUSINESS_UNIT_LIST } from './constants';
import { generateSearchUrl } from '../search/utils/search.util';
import {
  checkValidISBN,
  performSearchSpecificIsbn,
  performSearch,
  getSearchRequestFromUrl,
} from '../search/thunks/search.thunk';
import {
  performV2Search,
  getSearchRequestFromUrl as getSearchV2RequestFromUrl,
} from '../search-v2/thunks/perform-search-v2.thunk';
import {
  selectSortBy,
  isLoading,
  selectSelectedCatalog,
  searchParam,
  selectRequest,
} from '../search/store/search.selector';
import { searchParam as searchV2Param, selectFacets, selectV2Request } from '../search-v2/store/search-v2.selectors';
import { SearchV2Facet } from '../search-v2/interface/search-v2.interface';
import { getSearchPageAnalytics, checkBUParam } from '../../common/utils/analytics-util';
import { setSelectedCatalog } from '../search/store/search.store';
import { clearSuggestions } from '../search/store/suggestion.store';
import {
  getUniqueId,
  mapCatalog,
  reverseCatalogMap,
  wait,
  isSearchPage as isSearchPageUtil,
} from '../../common/utils/util';
import { isHeCustomStoreCart, isHePrivateStoreCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { saveSearchLocalStorageObject } from '../search/utils/search.util';
import { SearchTermAnalyticsConstants } from '../../common/constants/anayltics.constants';
import { TAB_NAMES } from '../tabbed-search/constants';

@customElement('mhecomm-search-v3')
export class SearchV3 extends connect(store)(BaseComponent) {
  @property()
  id = '';

  @property({ type: Number })
  pageSize = 5;

  /**
   * Parameter to determine the page the search component is getting called, default is empty string
   */
  @property({ type: String })
  searchPageType = '';

  /**
   * Determines which search results page to redirect to.
   * SearchResultPage.DEFAULT = /search.html (uses SearchV1 API)
   * SearchResultPage.SUPPORT = /support/search.html (uses SearchV2 API)
   */
  @property()
  searchResultPage: SearchResultPage = SearchResultPage.DEFAULT;

  @property({ type: String })
  placeholder?: string;

  /**
   * The store to search within.
   */
  @property()
  store: string | undefined;

  @state()
  searchInput = '';

  @state()
  businessUnit = 'products';

  @property()
  learningLevel: string | undefined;

  @property()
  product: string | undefined;

  @property()
  persona: string | undefined;

  @state()
  isLoading = false;

  @state()
  error: string | null = null;

  @state()
  showSearchSuggestion = false;

  // Default to empty string so stateChanged always processes the initial tab on first call
  @state()
  activeTab = '';

  @property({ type: Boolean })
  renderSmallUI = false;

  // Defaults to true, and then the stateChanged will update this as needed
  @state()
  private hide = true;

  @state()
  private isFieldValueFilterAvailable = false;

  /**
   * Owner tag propagated to `performV2Search` so the legacy search-v2 component (when
   * mounted on the same page) does not mistakenly emit its own `search-results-v2`
   * event for a request initiated by this SearchV3 instance. Generated once per
   * component instance — it is *not* per-request correlation. Concurrency between
   * consecutive submits of THIS component is handled via `supportInflightToken`.
   */
  @state()
  private readonly searchV2RequestOwnerId = getUniqueId();

  /**
   * Flag to prevent multiple search dispatches during the same tab switch cycle.
   * When SearchV3 initiates a search, it sets this to true to prevent TabbedSearchResult
   * from also triggering a search on the same tab change event.
   */
  @state()
  private isHandlingTabSearch = false;

  /**
   * Monotonic counter incremented on every Product/Articles dispatch. The token captured
   * at dispatch time is compared against `productArticlesInflightToken` after the awaited
   * response resolves; superseded (older) responses are discarded so analytics never
   * associate a stale total with the current search term.
   */
  private productArticlesRequestSeq = 0;

  /**
   * Token identifying the most recently initiated Product/Articles dispatch. Only the
   * dispatch whose captured token still equals this value at await-resolution time is
   * allowed to emit analytics.
   */
  private productArticlesInflightToken = 0;

  /**
   * Same pattern as `productArticlesRequestSeq` but for the Support (SearchV2) flow.
   */
  private supportRequestSeq = 0;

  /**
   * Same pattern as `productArticlesInflightToken` but for the Support (SearchV2) flow.
   */
  private supportInflightToken = 0;

  /**
   * URL-derived initial request for the SearchV1 (Product/Articles) flow.
   * Populated once in `initializeSearchFromUrl` on cold load, consumed and cleared
   * on the first `handleSearchPageAction` call so subsequent tab switches / user
   * actions use live Redux/component state instead of stale URL values.
   */
  @state()
  private urlHydratedRequest?: {
    start?: number;
    sortBy?: string;
    fieldValueFilter?: { [key: string]: Array<string> };
  };

  /**
   * URL-derived initial request for the SearchV2 (Support) flow. Same lifecycle as
   * `urlHydratedRequest` above but consumed by `loadSupportSearchResults`.
   */
  @state()
  private urlHydratedV2Request?: {
    start?: number;
    fieldValueFilter?: { [key: string]: Array<string> };
  };

  /**
   * A json object configuring search term redirects. If the user searches on one of the provided search terms
   * the user will be redirected to the configured page instead of doing a normal search.
   */
  @property({ type: Object })
  searchTermRedirects: SearchTermRedirects | undefined;

  connectedCallback(): void {
    // Populate URL hydration BEFORE super.connectedCallback(). The connect-mixin's
    // connectedCallback subscribes to the store AND fires `stateChanged(currentState)`
    // synchronously. Since the synchronous tab-switch handling in stateChanged (see
    // below) reads `this.urlHydratedRequest` / `this.urlHydratedV2Request` on the very
    // first bootstrap call, those caches must already be populated by that point —
    // otherwise the cold-load URL parameters (page/sortby/facets) are silently ignored
    // and the initial request goes out with defaults. See
    // /memories/repo/SearchV3-URL-hydration-on-cold-load.md.
    this.setBusinessUnit();
    if (this.isSearchPage()) {
      this.initializeSearchFromUrl();
    }
    super.connectedCallback();
    this._prePopulateSearchLSValues();
    if (this.searchResultPage === SearchResultPage.SUPPORT) {
      this.businessUnit = 'SUPPORT';
    }
  }

  /**
   * Initialize search input and parameters from URL on page load
   * This ensures the search component reflects the current URL state
   * URL parameters are stored in Redux store request objects instead of component state
   */
  private initializeSearchFromUrl(): void {
    try {
      // First, check for tab parameter in URL and set activeTab
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab') || 'Product';

      // Parse the URL using the same pageLength that `handleSearchPageAction` /
      // `loadSupportSearchResults` use for the results request (10). `this.pageSize`
      // is the suggestion dropdown size (default 5) and would produce a wrong `start`
      // when the URL has `page>1`, so we intentionally do not use it here.
      const resultsPageLength = 10;

      let searchRequest: any = {};

      if (tabParam === 'Support') {
        // Use SearchV2 function for Support tab
        searchRequest = getSearchV2RequestFromUrl(resultsPageLength);
        // Cache URL-derived values so the initial `loadSupportSearchResults` triggered
        // by stateChanged can honor `page` and `fieldValueFilter` from the URL instead
        // of falling back to defaults (start=1, fieldValueFilter={}). Consumed and
        // cleared on first use in `handleTabSwitchOnSearchPage`.
        this.urlHydratedV2Request = {
          start: typeof searchRequest.page === 'number' ? searchRequest.page : undefined,
          fieldValueFilter: searchRequest.fieldValueFilter || {},
        };
      } else {
        // Use SearchV1 function for Product/Articles tabs
        searchRequest = getSearchRequestFromUrl(resultsPageLength);
        // Same lifecycle as `urlHydratedV2Request` above — consumed by the initial
        // `handleSearchPageAction` call so `start`, `sortBy`, and `fieldValueFilter`
        // reflect the URL on cold load. Without this the initial request goes out
        // with start=1, sorts=[], filters={} even when the URL specifies otherwise.
        this.urlHydratedRequest = {
          start: typeof searchRequest.page === 'number' ? searchRequest.page : undefined,
          sortBy: searchRequest.sortby || undefined,
          fieldValueFilter: searchRequest.fieldValueFilter || {},
        };
      }

      // Update search input from URL
      if (searchRequest.searchQuery) {
        this.searchInput = searchRequest.searchQuery;
      }

      // Update business unit/catalog if available in URL.
      // The URL stores `bu` in lowercase (e.g. ?bu=he), which is already the businessUnit
      // format. Match it against BUSINESS_UNIT_LIST first; only fall back to reverseCatalogMap
      // for legacy uppercase values. The previous code called `reverseCatalogMap('he')` which
      // returned undefined (map keys are uppercase), so the businessUnit was never restored
      // from the URL after a topbar search that redirected here with ?bu=he.
      if (searchRequest.catalog) {
        const rawBu = String(searchRequest.catalog);
        const lowerBu = rawBu.toLowerCase();
        const isKnownBu = BUSINESS_UNIT_LIST.some((bu) => bu.value === lowerBu);
        const catalogBusinessUnit = isKnownBu ? lowerBu : reverseCatalogMap(rawBu.toUpperCase());
        if (catalogBusinessUnit) {
          this.businessUnit = catalogBusinessUnit;
        }
      }
    } catch (error) {
      console.error('Failed to initialize search from URL:', error);
    }
  }

  /**
   * Redux state change handler
   * Updates the component when active tab changes or selected catalog changes in the store.
   *
   * Tab-switch detection runs **synchronously** at the top (before `await wait(0)`) so
   * SearchV3's dispatch wins the race with `search-catalog-selector.updated()`, which
   * fires as a microtask after `setSelectedCatalog` and would otherwise call
   * `performSearch({start:1, fieldValueFilter:{}})` WITHOUT `sortBy` — leaking the
   * previous tab's `sortBy` (inherited from `oldRequest`) back into Redux state AND
   * back into the URL via `updateSearchUrl`. See
   * /memories/repo/SearchV3-pagination-sort-leak-across-tabs.md (Layer 2).
   */
  async stateChanged(state: StoreState): Promise<void> {
    // `activeTab === ''` only holds on the very first stateChanged invocation from
    // connect-mixin's `store.subscribe` bootstrap. Real tab switches always come in
    // with a non-empty previous `activeTab`. This distinguishes cold-load hydration
    // (URL is source of truth) from a real tab click (must reset URL to page=1/
    // sortby=relevance).
    const isBootstrap = this.activeTab === '';
    // On cold load the store still holds its default `Product` tab until
    // `tabbed-search-result` dispatches `setActiveTab`. Any earlier store notification
    // (e.g. `setSelectedCatalog`) would otherwise bootstrap this component onto the
    // Product tab and fire a product search for a `?tab=Articles` URL.
    const urlTab = isBootstrap && this.isSearchPage() ? this.getTabFromUrl() : undefined;
    const newTab = urlTab ?? state.tabbedSearchActiveTabState.activeTabKey;
    const isTabSwitchNow = this.activeTab !== newTab && !this.isHandlingTabSearch;

    if (isTabSwitchNow) {
      // Sync businessUnit from selectedCatalog BEFORE the tab-switch dispatch so
      // `handleSearchPageAction` sees a fresh businessUnit snapshot when resolving
      // the Product-tab catalog. See
      // /memories/repo/SearchV3-catalog-stale-on-tab-return.md.
      this.syncBusinessUnitFromCatalog(state, newTab);
      this.activeTab = newTab;
      if (this.isSearchPage()) {
        this.handleTabSwitchOnSearchPage(newTab, state, isBootstrap);
      } else {
        this.clearResults();
      }
    }

    await wait(0);

    // Keep BU in sync for non-tab-switch state changes (e.g., BU dropdown /
    // `setSelectedCatalog` from another component while on the same tab).
    if (!isTabSwitchNow) {
      this.syncBusinessUnitFromCatalog(state, newTab);
    }

    // Sync searchInput with search term from store based on active tab
    let storeSearchTerm = '';
    if (this.activeTab === 'Support') {
      // Use SearchV2 search term for Support tab
      storeSearchTerm = searchV2Param(state) || '';
    } else {
      // Use SearchV1 search term for Product/Articles tabs
      storeSearchTerm = searchParam(state) || '';
    }

    if (storeSearchTerm && this.searchInput !== storeSearchTerm) {
      this.searchInput = storeSearchTerm;
    }

    this.hide = isHeCustomStoreCart(state) || !!isHePrivateStoreCart(state);
  }

  /**
   * Reads a valid `tab` param from the URL, or `undefined` when absent/unrecognized.
   * Mirrors `tabbed-search-result.initializeFromUrl`, which infers the Articles tab from
   * `bu=articles|content` when no `tab` param is present.
   */
  private getTabFromUrl(): string | undefined {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') ?? '';
      if (Object.values(TAB_NAMES).includes(tab)) {
        return tab;
      }
      const bu = params.get('bu') ?? '';
      return bu === 'articles' || bu === 'content' ? TAB_NAMES.ARTICLES : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Syncs `businessUnit` from Redux `selectedCatalog`, guarding against overwrites by
   * non-BU tab markers (`Articles` / `Support`). Extracted so it can run synchronously
   * before the tab-switch dispatch AND after `await wait(0)` for in-tab updates.
   */
  private syncBusinessUnitFromCatalog(state: StoreState, newTab: string): void {
    const selectedCatalog = selectSelectedCatalog(state);
    if (!selectedCatalog) return;
    const catalogBusinessUnit = reverseCatalogMap(selectedCatalog);
    if (!catalogBusinessUnit || this.businessUnit === catalogBusinessUnit) return;
    if (newTab === 'Support') {
      this.businessUnit = 'SUPPORT';
    } else if (newTab !== 'Articles' && selectedCatalog !== 'ARTICLES' && selectedCatalog !== 'SUPPORT') {
      // Articles is a search dimension, not a business unit — writing
      // `businessUnit = 'articles'` would corrupt catalog resolution when the
      // user later returns to the Product tab (mapCatalog('articles') === 'ARTICLES').
      // Preserve the user's real BU when the active tab is Articles.
      this.businessUnit = catalogBusinessUnit;
    }
  }

  /**
   * Handles tab switching on search pages
   * SearchV3 is the coordinator for all search actions on the search page to prevent
   * duplicate searches and events from TabbedSearchResult component.
   *
   * @param isBootstrap `true` on the very first stateChanged invocation (initial store
   * subscribe from connect-mixin). Distinguishes cold-load hydration from a real tab
   * click. On bootstrap the URL is NOT rewritten — cold-load URL params
   * (`page`, `sortby`, facets) are the source of truth and must be preserved so
   * `initializeSearchFromUrl`'s cached hydration reflects them. On real tab switches
   * the URL is proactively reset to `page=1&sortby=relevance&tab=<newTab>` so any
   * cascaded dispatch from `search-catalog-selector` cannot leak previous-tab values
   * back through `updateSearchUrl`.
   *
   * The prior `isLoading` skip guard was removed intentionally: the request-identity
   * guards in `performSearch` / `performV2Search` and the `finally` token guards in
   * `handleSearchPageAction` / `loadSupportSearchResults` (memory item #5) discard any
   * superseded response, so overlapping in-flight requests are safe. Blocking on
   * `isLoading` here previously caused the fresh-tab race where a fast click during a
   * cold-load fetch skipped the new tab's dispatch entirely.
   */
  private handleTabSwitchOnSearchPage(newTab: string, _state: StoreState, isBootstrap = false): void {
    if (!this.isPrimaryVisibleInstanceForId()) {
      return;
    }

    // Reset URL to tab-switch defaults BEFORE any dispatch runs. This defends against
    // the microtask race with `search-catalog-selector.updated()` — even if that
    // cascade fires afterwards and its thunk writes to the URL via `updateSearchUrl`,
    // the response-identity guard in the thunk discards its result, and by then this
    // component's own dispatch has already updated Redux and the URL to correct values.
    if (!isBootstrap) {
      this.rewriteUrlForTabSwitch(newTab);
    }

    const searchTerm = this.searchInput.trim();

    if (newTab === 'Support') {
      this.isHandlingTabSearch = true;
      // Consume any URL-derived hydration exactly once on cold load. On real tab
      // switches this will be `undefined` and the tab-switch branch below fires,
      // which forces the SearchV2 request back to page 1 with empty filters.
      const v2Hydration = this.urlHydratedV2Request;
      this.urlHydratedV2Request = undefined;
      // Cross-clear the opposite-tab hydration so a subsequent switch back to
      // Product/Articles cannot accidentally re-hydrate stale URL values.
      this.urlHydratedRequest = undefined;
      const supportArgs =
        v2Hydration && (v2Hydration.start || Object.keys(v2Hydration.fieldValueFilter || {}).length > 0)
          ? {
              searchQuery: searchTerm,
              page: v2Hydration.start ?? 1,
              fieldValueFilter: v2Hydration.fieldValueFilter ?? {},
            }
          : {
              // Real tab switch to Support: explicitly reset page/filters and pass
              // `isTabSwitch: true` so loadSupportSearchResults does NOT fall back
              // to `currentV2Request.start` (which would leak the previous Support
              // pagination and cause the SearchV2 thunk to rewrite the URL with the
              // stale page number).
              searchQuery: searchTerm,
              page: 1,
              fieldValueFilter: {},
              isTabSwitch: true,
            };
      this.loadSupportSearchResults(supportArgs).finally(() => {
        this.isHandlingTabSearch = false;
      });
    } else {
      // Load Product/Articles results using SearchV1 API with URL parameters.
      // On cold load, consume the URL-derived hydration exactly once so `start`,
      // `sortBy`, and `fieldValueFilter` reflect the URL. On real tab switches
      // `urlHydratedRequest` is undefined and handleSearchPageAction resets
      // start/sortBy/fieldValueFilter because `overrideTab` is truthy.
      this.isHandlingTabSearch = true;
      const hydration = this.urlHydratedRequest;
      this.urlHydratedRequest = undefined;
      // Cross-clear the opposite-tab hydration so a subsequent switch back to
      // Support cannot accidentally re-hydrate stale URL values.
      this.urlHydratedV2Request = undefined;
      this.handleSearchPageAction(searchTerm, false, newTab, hydration).finally(() => {
        this.isHandlingTabSearch = false;
      });
    }
  }

  /**
   * Proactively resets the URL to tab-switch defaults (`page=1`, `sortby=relevance`,
   * `tab=<newTab>`) before dispatching the new tab's request. This guarantees the URL
   * reflects the intended reset even if a microtask-scheduled cascade from another
   * component (e.g. `search-catalog-selector.updated()`) subsequently calls
   * `updateSearchUrl` with stale values inherited from the previous request.
   *
   * Preserves `searchQuery`, `bu`, and facet params — those are handled by the
   * dispatch itself and by cross-tab reset logic in `handleSearchPageAction`.
   */
  private rewriteUrlForTabSwitch(newTab: string): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', '1');
      url.searchParams.set('sortby', 'relevance');
      url.searchParams.set('tab', newTab);
      window.history.pushState({}, '', url.toString());
    } catch {
      // URL manipulation should never break tab switching.
    }
  }

  private setBusinessUnit(): void {
    const path = window.location.pathname;
    this.businessUnit = Object.keys(BUSINESS_UNIT_MAP).reduce(
      (unit, key) => (path.includes(key) ? BUSINESS_UNIT_MAP[key] : unit),
      this.businessUnit
    );
  }

  private getCatalog(): string {
    switch (this.activeTab) {
      case 'Articles':
        return 'ARTICLES';
      case 'Support':
        return 'SUPPORT';
      case 'Product':
      default:
        return mapCatalog(this.businessUnit) || 'UNIFIED';
    }
  }

  private updateSearchInput(value: string) {
    this.searchInput = value;
  }

  /**
   * Search handler for Product/Articles tabs (SearchV1 API)
   */
  async handleProductSearchInputChange(value: string) {
    this.searchInput = value;

    // Early return for invalid search terms
    if (value.length <= 2 || checkValidISBN(value)) {
      return [];
    }

    this.isLoading = true;
    this.error = null;

    try {
      return await this.fetchSearchV1Results(value);
    } catch (err) {
      this.error = 'Failed to fetch results';
      console.error(this.error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Unified search input handler that selects correct API based on searchResultPage
   * - SearchResultPage.SUPPORT → SearchV2 API (no ISBN check)
   * - SearchResultPage.DEFAULT → SearchV1 API (with ISBN check)
   */
  async handleSearchInputChange(value: string) {
    this.searchInput = value;

    // Early return for invalid search terms
    if (value.length <= 2) {
      return [];
    }

    // Skip ISBN check for support search
    if (this.searchResultPage !== SearchResultPage.SUPPORT && checkValidISBN(value)) {
      return [];
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Choose API based on searchResultPage flag
      if (this.searchResultPage === SearchResultPage.SUPPORT) {
        return await this.fetchSearchV2Results(value);
      } else {
        return await this.fetchSearchV1Results(value);
      }
    } catch (err) {
      this.error = 'Failed to fetch results';
      console.error(this.error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Search handler for Support tab (SearchV2 API)
   */
  async handleSupportSearchInputChange(value: string) {
    this.searchInput = value;

    // Early return for invalid search terms (no ISBN check for Support)
    if (value.length <= 2) {
      return [];
    }

    this.isLoading = true;
    this.error = null;

    try {
      return await this.fetchSearchV2Results(value);
    } catch (err) {
      this.error = 'Failed to fetch results';
      console.error(this.error);
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  async handleDropdownChange(value: string) {
    this.businessUnit = value;

    if (this.isSearchPage()) {
      // Update Redux store with the selected catalog using existing mapCatalog utility
      const catalogValue = mapCatalog(value || 'unified');
      await store.dispatch(setSelectedCatalog(catalogValue));

      // Check if search is already loading to avoid duplicate searches
      const state = store.getState() as StoreState;
      const isAlreadyLoading = isLoading(state);

      if (!isAlreadyLoading) {
        // Perform search with updated catalog similar to search-catalog-selector
        const result = await store.dispatch(
          performSearch({
            start: 1,
            fieldValueFilter: {},
            categoryId: undefined,
          })
        );

        // Clear suggestions when catalog changes
        store.dispatch(clearSuggestions());

        // Save search analytics to localStorage if results are available
        const totalResults = result ? result.total : 0;
        if (totalResults !== undefined) {
          this._saveSearchLSObject(totalResults);
        }
      }
    }

    SearchV3Analytics.businessUnit({
      element: this,
      id: this.id,
      label: 'businessUnit',
      term: this.searchInput,
      businessUnit: this.businessUnit,
      searchPageType: this.searchPageType,
      activeTab: this.activeTab,
    });
  }

  async handleDropdownOpen() {
    SearchV3Analytics.businessUnit({
      element: this,
      id: this.id,
      label: 'businessUnitDropDown',
      term: this.searchInput,
      businessUnit: this.businessUnit,
      searchPageType: this.searchPageType,
      activeTab: this.activeTab,
    });
  }

  async onSearchTrigger() {
    let redirectUrl: string | undefined;
    const searchTermRedirectUrl = getSearchTermRedirect({
      searchTermRedirects: this.searchTermRedirects,
      term: this.searchInput,
      businessUnit: this.businessUnit,
    });

    redirectUrl = searchTermRedirectUrl;

    if (!redirectUrl && this.searchResultPage !== SearchResultPage.SUPPORT) {
      redirectUrl = (await performSearchSpecificIsbn({ catalog: this.getCatalog(), searchTerms: this.searchInput }))
        ?.urlToGo;
    }

    if (!redirectUrl) {
      redirectUrl = generateSearchUrl({
        searchQuery: this.searchInput,
        businessUnit: this.businessUnit,
        product: this.product,
        userType: this.persona,
        learningLevel: this.learningLevel,
        searchResultPage: this.searchResultPage,
      });
    }

    SearchV3Analytics.search({
      element: this,
      id: this.id,
      term: this.searchInput,
      businessUnit: this.businessUnit,
      redirectUrl,
      storeCategory: this.store,
      searchPageType: this.searchPageType,
      activeTab: this.activeTab,
    });

    if (searchTermRedirectUrl) {
      SearchV3Analytics.searchTermRedirect({
        element: this,
        id: this.id,
        term: this.searchInput,
        redirectUrl: searchTermRedirectUrl,
        searchPageType: this.searchPageType,
        searchText: getSearchPageAnalytics(this.searchInput, 0),
      });
    }

    if (redirectUrl) {
      this.navigateTo(redirectUrl);
    }
  }

  /**
   * Handles search actions for search page behavior
   * For Product tab: Checks for redirect URLs first, otherwise dispatches search actions to load results in place
   * For other tabs: Always loads results in place
   * Similar to loadSearchResults method in the original search.ts component
   *
   * @param searchTerm Search text to execute.
   * @param emitSearchEvent Emits the base `search` analytics event for user-initiated submits.
   * Keep this false for system-driven refreshes (tab switch/page initialization) to avoid duplicate events.
   * Redirect term matches still emit `search` even when this flag is false.
   * @param overrideTab Force the tab context for catalog resolution (used on tab-switch bootstrap).
   * @param hydrationOverrides URL-derived initial values (`start`, `sortBy`, `fieldValueFilter`).
   * When provided, they take precedence over `selectRequest(state)` and the `isTabSwitch`
   * filter-reset is skipped — the cold-load hydration is not a real tab switch and its
   * filters are already scoped to the correct catalog by construction.
   */
  async handleSearchPageAction(
    searchTerm: string,
    emitSearchEvent = false,
    overrideTab?: string,
    hydrationOverrides?: {
      start?: number;
      sortBy?: string;
      fieldValueFilter?: { [key: string]: Array<string> };
    }
  ): Promise<void> {
    const effectiveTab = overrideTab || this.activeTab;
    // Update the search input
    this.searchInput = searchTerm;
    let redirectUrl: string | undefined;
    // Only check for redirects on Product tab
    if (this.activeTab === 'Product') {
      redirectUrl = getSearchTermRedirect({
        searchTermRedirects: this.searchTermRedirects,
        term: searchTerm,
        businessUnit: this.businessUnit,
      });

      // If redirect URL exists, navigate to it instead of loading results in place
      if (redirectUrl) {
        SearchV3Analytics.search({
          element: this,
          id: this.id,
          term: searchTerm,
          businessUnit: this.businessUnit,
          redirectUrl,
          storeCategory: this.store,
          searchPageType: this.searchPageType,
          activeTab: this.activeTab,
        });

        SearchV3Analytics.searchTermRedirect({
          element: this,
          id: this.id,
          term: searchTerm,
          redirectUrl,
          searchPageType: this.searchPageType,
          searchText: getSearchPageAnalytics(searchTerm, 0),
        });

        this.navigateTo(redirectUrl);
        return;
      }
    }

    if (emitSearchEvent) {
      SearchV3Analytics.search({
        element: this,
        id: this.id,
        term: searchTerm,
        businessUnit: this.businessUnit,
        redirectUrl: undefined,
        storeCategory: this.store,
        searchPageType: this.searchPageType,
        activeTab: this.activeTab,
      });
    }

    // Declared outside `try` so the `finally` block can gate `isLoading` cleanup
    // to only the most-recent dispatch. Assigned right before the awaited dispatch.
    let token = 0;
    try {
      this.isLoading = true;
      this.error = null;

      const state = store.getState() as StoreState;
      const currentSort = selectSortBy(state);
      // Compute catalog from effectiveTab to avoid stale this.businessUnit (e.g. 'SUPPORT' after tab switch)
      const searchCatalog = (() => {
        switch (effectiveTab) {
          case 'Articles':
            return 'ARTICLES';
          case 'Support':
            return 'SUPPORT';
          case 'Product':
          default: {
            // Defense-in-depth: on a real Product tab-switch (overrideTab set AND no URL
            // hydration in play), prefer the store's `selectedCatalog` since it reflects
            // the latest dispatch from `tabbed-search-result` and is guaranteed fresh
            // relative to the tab click that triggered this call. Cold-load hydration
            // uses businessUnit-derived catalog (URL is source of truth on cold load), so
            // it must bypass this path — otherwise a stale store value could clobber the
            // URL-derived catalog. Also skip when the store value is a non-BU tab marker
            // ('ARTICLES'/'SUPPORT') that would produce an invalid Product request.
            const storeCatalog = overrideTab && !hydrationOverrides ? selectSelectedCatalog(state) : undefined;
            if (storeCatalog && storeCatalog !== 'ARTICLES' && storeCatalog !== 'SUPPORT') {
              return storeCatalog;
            }
            const mapped = mapCatalog(this.businessUnit);
            return mapped && mapped !== 'SUPPORT' && mapped !== 'ARTICLES' ? mapped : 'UNIFIED';
          }
        }
      })();

      // Read URL parameters from Redux store request object instead of component state
      const currentRequest = selectRequest(state);
      const pageLength = 10;
      // `start`, `sortBy`, and `fieldValueFilter` are ALL per-catalog. On a real tab switch
      // (overrideTab set, no hydration) we must reset all three so the new tab starts at
      // page 1 with the default sort and no leftover facets from the previous catalog.
      // Without this reset, the URL rewrite performed by `updateSearchUrl` inside the
      // performSearch thunk regrabs `start`/`sortBy` from `currentRequest` and clobbers
      // the page=1/sortby=default reset that `tabbed-search-result.selectTab` writes to
      // the URL just before this dispatch. See
      // /memories/repo/SearchV3-pagination-sort-leak-across-tabs.md.
      //
      // Hydration overrides (cold-load from URL) take precedence over both branches — the
      // URL is source of truth on first paint and its values are already scoped to the
      // correct catalog by construction.
      const isTabSwitch = !!overrideTab;
      const start = hydrationOverrides?.start ?? (isTabSwitch ? 1 : currentRequest?.start ?? 1);
      const sortBy =
        hydrationOverrides?.sortBy ??
        (isTabSwitch ? 'relevance' : currentRequest?.sortBy ?? currentSort ?? 'relevance');
      const fieldValueFilter =
        hydrationOverrides?.fieldValueFilter ?? (isTabSwitch ? {} : currentRequest?.fieldValueFilter || {});

      // Capture a token for this dispatch so we can reject responses that arrive after
      // a newer submit has superseded them (out-of-order completion race).
      token = ++this.productArticlesRequestSeq;
      this.productArticlesInflightToken = token;

      // Dispatch performSearch action to load results in place.
      // isCatalogSet: true because `searchCatalog` was derived above from this component's
      // businessUnit (which itself is derived from the URL/pathname). If we left it false,
      // getSearchRequest would overwrite `catalog` with the store's `selectedCatalog`, which
      // can be stale or in the middle of being updated by another component (e.g.
      // tabbed-search-result dispatching setSelectedCatalog('PRODUCTS')) — that race caused
      // requests to go out with catalog='PRODUCTS' when the user's actual selection was 'HE'.
      const result = await store.dispatch(
        performSearch({
          searchTerms: searchTerm,
          catalog: searchCatalog,
          pageLength: pageLength,
          start: start,
          sortBy: sortBy,
          facets: true,
          store: this.store,
          fieldValueFilter: fieldValueFilter,
          excludeFacetName: undefined,
          clearFilter: true,
          clearSort: true,
          isCatalogSet: true,
          predefinedFilters: [],
        })
      );

      // Superseded response — a newer dispatch was issued after us. Discarding it here
      // prevents analytics from associating this response's total with the current
      // (newer) `searchTerm` in the input.
      if (token !== this.productArticlesInflightToken) {
        return;
      }

      // `performSearch` swallows errors and returns undefined on failure — skip emission
      // in that case to avoid reporting a `total: 0` that reflects failure, not results.
      if (!result) {
        return;
      }

      const totalResults = typeof result.total === 'number' ? result.total : 0;
      this.emitProductArticlesResults({
        term: searchTerm,
        totalResults,
      });
      this._saveSearchLSObject(totalResults);
    } catch (error) {
      this.error = 'Failed to perform search';
      console.error('Search page action failed:', error);
    } finally {
      // Only the most-recent dispatch may clear the loading flag. A superseded
      // dispatch that resolves late must not turn the spinner off while a newer
      // request is still in flight.
      if (token === this.productArticlesInflightToken) {
        this.isLoading = false;
      }
    }
  }

  /**
   * Handles search button trigger on search pages
   * Calls handleSearchPageAction with the current search input
   */
  async onSearchPageTrigger(): Promise<void> {
    await this.handleSearchPageAction(this.searchInput.trim(), true);
  }

  /**
   * Handles search button trigger on search pages for Support tab
   * Always loads results in place (no redirect logic for Support tab)
   */
  async onSupportSearchPageTrigger(): Promise<void> {
    const term = this.searchInput.trim();

    SearchV3Analytics.search({
      element: this,
      id: this.id,
      term,
      businessUnit: this.businessUnit,
      storeCategory: this.store,
      searchPageType: this.searchPageType,
      activeTab: this.activeTab,
    });

    await this.loadSupportSearchResults({
      searchQuery: term,
      page: 1,
      fieldValueFilter: {},
    });
  }

  /**
   * Loads search results for Support tab using SearchV2 API
   * Similar to loadSearchResults method in search-v2 component
   *
   * @param isTabSwitch When true, `page` and `fieldValueFilter` from the caller are
   * honored verbatim and the method does NOT fall back to `currentV2Request` values.
   * Real tab switches (from `handleTabSwitchOnSearchPage`) must pass `isTabSwitch: true`
   * so previous Support pagination/filters do not leak into the fresh tab request.
   * See /memories/repo/SearchV3-pagination-sort-leak-across-tabs.md.
   */
  async loadSupportSearchResults({
    searchQuery = '',
    page = 1,
    fieldValueFilter = {},
    isTabSwitch = false,
  }: {
    searchQuery?: string;
    page?: number;
    fieldValueFilter?: { [key: string]: Array<string> };
    isTabSwitch?: boolean;
  } = {}): Promise<void> {
    // Declared outside `try` so the `finally` block can gate `isLoading` cleanup
    // to only the most-recent dispatch. Assigned right before the awaited dispatch.
    let token = 0;
    try {
      if (!this.isPrimaryVisibleInstanceForId()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      // Read URL parameters from Redux store request object instead of component state
      const state = store.getState() as StoreState;
      const currentV2Request = selectV2Request(state);

      const finalSearchQuery = searchQuery || this.searchInput;
      // On tab switch, honor the caller-supplied `page` verbatim (defaults to 1) so
      // stale `currentV2Request.start` from a previous Support session cannot leak.
      // Otherwise keep the legacy behavior: page=1 falls back to the store's start.
      const finalPage = isTabSwitch ? page : page !== 1 ? page : currentV2Request?.start || 1;

      // Handle fieldValueFilter type properly
      let finalFieldValueFilter: { [key: string]: Array<string> } = {};
      if (isTabSwitch) {
        // Tab switch: use exactly what the caller passed (defaults to {}). No fallback.
        finalFieldValueFilter = fieldValueFilter;
      } else if (Object.keys(fieldValueFilter).length > 0) {
        finalFieldValueFilter = fieldValueFilter;
      } else if (currentV2Request?.fieldValueFilter) {
        // Ensure it's the right type
        if (Array.isArray(currentV2Request.fieldValueFilter)) {
          finalFieldValueFilter = {};
        } else {
          finalFieldValueFilter = currentV2Request.fieldValueFilter as { [key: string]: Array<string> };
        }
      }

      this.isFieldValueFilterAvailable = Object.keys(finalFieldValueFilter).length > 0;

      // Capture a token for this dispatch so we can reject responses that arrive after
      // a newer submit has superseded them (out-of-order completion race).
      token = ++this.supportRequestSeq;
      this.supportInflightToken = token;

      // Dispatch performV2Search for Support tab. `ownerId` isolates this component's
      // request from the legacy search-v2 component's stateChanged emitter — it is NOT
      // a per-request correlator (see `searchV2RequestOwnerId` field docs).
      const searchData = await store.dispatch(
        performV2Search({
          ownerId: this.searchV2RequestOwnerId,
          searchTerms: finalSearchQuery,
          pageLength: 10,
          start: finalPage,
          facets: true,
          fieldValueFilter: finalFieldValueFilter,
        })
      );

      // Superseded response — discard so analytics never associate this response's total
      // with the current (newer) search term. See `handleSearchPageAction` for the same
      // pattern on the SearchV1 path.
      if (token !== this.supportInflightToken) {
        return;
      }

      // performV2Search returns undefined on failure. Skip emission in that case.
      if (!searchData) {
        return;
      }

      const totalResults = typeof searchData?.page?.totalDocuments === 'number' ? searchData.page.totalDocuments : 0;
      const emittedFilters = this.isFieldValueFilterAvailable ? {} : this.filterValues();
      this.emitSupportResults({
        term: finalSearchQuery,
        totalResults,
        fieldValueFilter: emittedFilters,
      });
      this._saveSearchLSObject(totalResults);
    } catch (error) {
      this.error = 'Failed to perform support search';
      console.error('Support search failed:', error);
    } finally {
      // Only the most-recent dispatch may clear the loading flag. See the twin
      // guard in `handleSearchPageAction` for the SearchV1 path.
      if (token === this.supportInflightToken) {
        this.isLoading = false;
      }
    }
  }

  searchResultClickAnalytic({ redirectUrl, term }: SearchV3Result) {
    SearchV3Analytics.searchSuggestion({
      element: this,
      id: this.id,
      term,
      businessUnit: this.getCatalog() || '',
      redirectUrl: this.isSearchPage() ? undefined : redirectUrl,
      searchPageType: this.searchPageType,
      activeTab: this.activeTab,
    });
  }

  /**
   * Fetch results using SearchV1 API (existing logic for Product/Articles)
   */
  private async fetchSearchV1Results(value: string): Promise<SearchV3Result[]> {
    const catalog = this.getCatalog();

    return await getSuggestionsApiCall(value, catalog).then((searchResult) =>
      searchV3SuggestionMapper({
        searchResult,
        searchTermRedirects: this.searchTermRedirects,
        selectedBusinessUnit: this.businessUnit,
        searchResultPage: this.searchResultPage,
        product: this.product,
        learningLevel: this.learningLevel,
        userType: this.persona,
      })
    );
  }

  /**
   * Fetch results using SearchV2 API (for Support tab)
   */
  private async fetchSearchV2Results(value: string): Promise<SearchV3SupportResult[]> {
    const searchTerms = value;

    return await getSuggestionsV2ApiCall(searchTerms)
      .then((response) => response.json())
      .then((searchResult: string[]) =>
        searchV2SuggestionMapper({
          searchResult,
          searchResultPage: this.searchResultPage,
          businessUnit: this.businessUnit,
          isSearchPage: this.isSearchPage(),
          searchTermRedirects: this.searchTermRedirects,
          product: this.product,
          learningLevel: this.learningLevel,
          userType: this.persona,
        })
      );
  }

  clearResults() {
    this.searchInput = '';
    this.error = '';
  }

  private _prePopulateSearchLSValues() {
    const resetSearchLSKey = SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE;
    const professionalProductsMapperKey = SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER;
    try {
      if (localStorage.getItem(resetSearchLSKey) === null) {
        localStorage.setItem(resetSearchLSKey, 'false');
      }
      if (localStorage.getItem(professionalProductsMapperKey) === null) {
        localStorage.setItem(professionalProductsMapperKey, JSON.stringify({}));
      }
    } catch (error) {
      console.error('Error in _prePopulateSearchLSValues.');
    }
  }

  private _saveSearchLSObject(totalResults: number) {
    if (!checkBUParam()) {
      saveSearchLocalStorageObject({
        searchTerm: this.searchInput ? this.searchInput : 'no query',
        totalResults,
      });
    }
  }

  /**
   * Get applied field value filters from search-v2 facets
   * Similar to filterValues method in search-v2 component
   */
  private filterValues(): { [key: string]: Array<string> } {
    const state = store.getState() as StoreState;
    const facets: Array<SearchV2Facet> = selectFacets(state)!;
    const appliedFilter: { [key: string]: Array<string> } = {};
    facets?.forEach((facet) => {
      const values: Array<string> = facet?.facetValues
        .filter((facetValue) => facetValue.selected)
        .map((val) => val.name);
      if (values.length > 0) {
        appliedFilter[facet.name] = values;
      }
    });
    return appliedFilter;
  }

  /**
   * Emits the `search-results` analytics event for the Product/Articles (SearchV1) flow.
   * Called from `handleSearchPageAction` after the dispatched request has resolved and
   * the token guard confirmed the response is still the latest one.
   */
  private emitProductArticlesResults({ term, totalResults }: { term: string; totalResults: number }): void {
    if (!this.isPrimaryVisibleInstanceForId()) {
      return;
    }
    const businessUnitObj = BUSINESS_UNIT_LIST.find((bu) => bu.value === this.businessUnit);
    SearchV3Analytics.searchResults({
      element: this,
      id: 'searchV3',
      term,
      store: this.businessUnit,
      totalResults,
      searchPageType: 'Search Results',
      catalog: businessUnitObj?.label || '',
      searchText: getSearchPageAnalytics(term, totalResults),
      activeTab: this.activeTab,
    });
  }

  /**
   * Emits the `search-results-v2` analytics event for the Support (SearchV2) flow.
   * Called from `loadSupportSearchResults` after the dispatched request has resolved and
   * the token guard confirmed the response is still the latest one.
   */
  private emitSupportResults({
    term,
    totalResults,
    fieldValueFilter,
  }: {
    term: string;
    totalResults: number;
    fieldValueFilter: { [key: string]: Array<string> };
  }): void {
    if (!this.isPrimaryVisibleInstanceForId()) {
      return;
    }
    SearchV3Analytics.searchResultsV2({
      element: this,
      id: this.id,
      term,
      store: this.store,
      totalResults,
      searchPageType: this.searchPageType,
      fieldValueFilter,
    });
  }

  /** Wrapped for testability — allows stubbing navigation in unit tests without triggering WTR page interruptions. */
  protected navigateTo(url: string) {
    window.location.href = url;
  }

  isSearchPage() {
    return isSearchPageUtil();
  }

  private isPrimaryVisibleInstanceForId(): boolean {
    if (!this.id) {
      return true;
    }

    const escapedId = CSS?.escape ? CSS.escape(this.id) : this.id;
    const instances = Array.from(document.querySelectorAll(`mhecomm-search-v3[id="${escapedId}"]`)) as SearchV3[];
    if (instances.length <= 1) {
      return true;
    }

    const visibleInstances = instances.filter(
      (instance) => instance.getClientRects().length > 0 || instance.offsetParent !== null
    );

    if (visibleInstances.length > 0) {
      return visibleInstances[0] === this;
    }

    return instances[0] === this;
  }

  getPlaceholderText() {
    if (this.placeholder) {
      return this.placeholder;
    }

    if (this.businessUnit === 'seg') {
      return 'Search by ISBN, MHID, Title, State or Subject';
    } else {
      return 'Search by ISBN, Title, Author, Keyword';
    }
  }

  /**
   * Determines if business unit dropdown should be shown
   * Hidden for support search pages
   */
  shouldShowBusinessUnitDropdown(): boolean {
    return this.searchResultPage !== SearchResultPage.SUPPORT;
  }

  renderComp() {
    // On search pages, hide the component on mobile (d-none) but show on medium+ screens (d-md-block)
    // Also hide completely if custom/private store cart is detected
    const cssClasses = this.hide ? 'd-none' : this.isSearchPage() ? 'd-none d-md-block' : '';

    if (this.isSearchPage()) {
      if (this.activeTab === 'Support') {
        // Render Support-specific UI component
        return html`
          <mhecomm-search-v3-ui-support
            class=${cssClasses}
            id=${this.id}
            searchPlaceholder=${this.getPlaceholderText()}
            .searchInput=${this.searchInput}
            .dropdownValue=${this.businessUnit}
            .isLoading=${this.isLoading}
            .renderSmallUI=${this.renderSmallUI}
            .showBusinessUnitDropdown=${this.shouldShowBusinessUnitDropdown()}
            .updateSearchInput=${this.updateSearchInput.bind(this)}
            .onDropdownChange=${this.handleDropdownChange.bind(this)}
            .onDropdownOpen=${this.handleDropdownOpen.bind(this)}
            .searchResultClickAnalytic=${this.searchResultClickAnalytic.bind(this)}
            .onSearchInputChange=${this.handleSupportSearchInputChange.bind(this)}
            .onSearchTrigger=${this.onSupportSearchPageTrigger.bind(this)}></mhecomm-search-v3-ui-support>
        `;
      } else {
        // Render search page UI component for in-place results (hidden on mobile)
        return html`
          <mhecomm-search-v3-ui-search-page
            class=${cssClasses}
            id=${this.id}
            searchPlaceholder=${this.getPlaceholderText()}
            .searchInput=${this.searchInput}
            .dropdownValue=${this.businessUnit}
            .isLoading=${this.isLoading}
            .renderSmallUI=${this.renderSmallUI}
            .showBusinessUnitDropdown=${this.shouldShowBusinessUnitDropdown()}
            .updateSearchInput=${this.updateSearchInput.bind(this)}
            .onDropdownChange=${this.handleDropdownChange.bind(this)}
            .onDropdownOpen=${this.handleDropdownOpen.bind(this)}
            .searchResultClickAnalytic=${this.searchResultClickAnalytic.bind(this)}
            .onSearchInputChange=${this.handleProductSearchInputChange.bind(this)}
            .onSearchTrigger=${this.onSearchPageTrigger.bind(this)}></mhecomm-search-v3-ui-search-page>
        `;
      }
    } else {
      // Render redirect UI component for Product/Articles tabs (default navigation behavior)
      return html`
        <mhecomm-search-v3-ui-redirect
          class=${cssClasses}
          id=${this.id}
          searchPlaceholder=${this.getPlaceholderText()}
          .searchInput=${this.searchInput}
          .dropdownValue=${this.businessUnit}
          .isLoading=${this.isLoading}
          .renderSmallUI=${this.renderSmallUI}
          .showBusinessUnitDropdown=${this.shouldShowBusinessUnitDropdown()}
          .updateSearchInput=${this.updateSearchInput.bind(this)}
          .onDropdownChange=${this.handleDropdownChange.bind(this)}
          .onDropdownOpen=${this.handleDropdownOpen.bind(this)}
          .searchResultClickAnalytic=${this.searchResultClickAnalytic.bind(this)}
          .onSearchInputChange=${this.handleSearchInputChange.bind(this)}
          .onSearchTrigger=${this.onSearchTrigger.bind(this)}></mhecomm-search-v3-ui-redirect>
      `;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v3': SearchV3;
  }
}
